import { useEffect, useState } from 'react';
import useClient from 'api/client';
import { useParams } from 'react-router-dom';
import generateEnvironmentAccessToken from 'api/Environment/generateEnvironmentAccessToken';
import queryAccountsWithEnvironmentURIs from 'api/Environment/queryAccountsWithEnvironmentURIs';
import getEnvironmentAssumeRoleUrl from 'api/Environment/getEnvironmentAssumeRoleUrl';
import listContextGroupTypes from 'api/Environment/listContextGroupTypes';
import { Credential } from 'types/Credentials';
import { useAlert } from 'components/message/AlertContext';
import formatCredentials, {
    FormatType,
} from 'components/modal/CredentialsModal/FormatCredentials';

export default function useEnvironmentCredentials(uri: string | undefined) {
    const client = useClient();

    const params = useParams<{ uri: string }>();
    const environmentUri = uri || params.uri || '';

    const { openAlert } = useAlert();
    const [loading, setLoading] = useState(true);
    const [urlLoading, setUrlLoading] = useState(false);
    const [credentialLoading, setCredentialsLoading] = useState(false);
    const [roleOptions, setRoleOptions] = useState([]);
    const [environmentURIs, setEnvironmentURIs] = useState<{
        [k: string]: string | null;
    }>();
    const [accountOptions, setAccountOptions] = useState<
        {
            name: string;
            label: string;
        }[]
    >([]);

    const fetchAccountsWithEnvironmentURIs = async () => {
        if (environmentUri) {
            const response = await client.query(
                queryAccountsWithEnvironmentURIs(environmentUri),
            );
            if (!response.errors) {
                // Accounts

                const accountKeys = Object.keys(
                    response.data.getEnvironment,
                ).filter((key) => key.includes('AccountId'));

                setAccountOptions(
                    accountKeys
                        .filter((key) => {
                            const value = response.data.getEnvironment[key];
                            return (
                                typeof value === 'string' && value.trim() !== ''
                            );
                        })
                        .map((key) => {
                            const environmentType = key
                                .replace(/^Aws|AccountId$/g, '')
                                .toUpperCase();

                            const account = response.data.getEnvironment[key];

                            return {
                                name: environmentType + ' ' + account,
                                label: environmentType + ' ' + account,
                            };
                        }),
                );

                // Environment URIs

                const environmentURIKeys = Object.keys(
                    response.data.getEnvironment,
                ).filter((key) => key.includes('EnvironmentUri'));

                setEnvironmentURIs(
                    Object.fromEntries(
                        environmentURIKeys.map((key) => {
                            const environmentType = key.replace(
                                'EnvironmentUri',
                                '',
                            );
                            return [
                                environmentType,
                                response.data.getEnvironment[key],
                            ];
                        }),
                    ),
                );
            } else {
                openAlert({
                    message: 'Accounts could not retrieved',
                    severity: 'alert',
                });
            }
            setLoading(false);
        }
    };

    const fetchGroups = async () => {
        if (environmentUri) {
            const response = await client.query(
                listContextGroupTypes(environmentUri),
            );
            if (!response.errors) {
                const roleOptions = response.data.listContextGroupTypes.map(
                    (item: string) => ({
                        name: item,
                        label: item,
                    }),
                );
                setRoleOptions(roleOptions);
            } else {
                openAlert({
                    message: 'Groups could not retrieved',
                    severity: 'alert',
                });
            }
        }
    };

    useEffect(() => {
        if (client) {
            setLoading(true);
            Promise.all([
                fetchAccountsWithEnvironmentURIs(),
                fetchGroups(),
            ]).finally(() => setLoading(false));
        }
    }, [client, environmentUri]);

    const getAWSUrl = async (
        environmentType: string,
        currentRole: string,
        currentAccount: string,
    ) => {
        setUrlLoading(true);
        const response = await client.query(
            getEnvironmentAssumeRoleUrl({
                environmentUri: environmentURIs?.[environmentType] || '',
                currentRole,
                currentAccount,
            }),
        );
        setUrlLoading(false);
        if (!response.errors) {
            window.open(response.data.getEnvironmentAssumeRoleUrl, '_blank');
        } else {
            openAlert({
                message: 'Environment URL could not be retrieved',
                severity: 'alert',
            });
        }
    };
    const getCredentials = async (
        environmentType: string,
        currentRole: string,
        currentAccount: string,
    ) => {
        setCredentialsLoading(true);
        try {
            const response = await client.query(
                generateEnvironmentAccessToken({
                    environmentUri: environmentURIs?.[environmentType] || '',
                    currentRole,
                    currentAccount,
                }),
            );
            if (!response.errors) {
                const inputString =
                    response.data.generateEnvironmentAccessToken;
                const formattedScript = formatCredentials(
                    inputString,
                    FormatType.Script,
                );
                const formattedString = formatCredentials(
                    inputString,
                    FormatType.String,
                );
                const copyCredentials = [
                    formattedScript as string,
                    formattedString as string,
                ];
                const formattedObject = formatCredentials(
                    inputString,
                    FormatType.Object,
                );
                const credentials = formattedObject as Credential[];

                return {
                    credentials,
                    copyCredentials,
                };
            } else {
                openAlert({
                    message: 'Environment access token could not be generated',
                    severity: 'alert',
                });
                return null;
            }
        } finally {
            setCredentialsLoading(false);
        }
    };

    return {
        loading,
        accountOptions,
        roleOptions,
        urlLoading,
        credentialLoading,
        getCredentials,
        getAWSUrl,
    };
}
